import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { authAPI } from '../services/apiService';
import { User, CreditCard, Smartphone, Camera } from 'lucide-react';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Profile data
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: ''
  });

  // Bank details
  const [bankDetails, setBankDetails] = useState({
    accountNumber: '',
    ifscCode: '',
    branchLocation: '',
    bankName: '',
    accountHolderName: ''
  });

  // UPI details
  const [upiAddress, setUpiAddress] = useState('');
  
  // Profile photo
  const [profilePhoto, setProfilePhoto] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    loadUserData();
  }, [navigate]);

  const loadUserData = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      
      // Set profile data
      setProfileData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        mobile: userData.mobile || ''
      });
      
      // Set bank details if exists
      if (userData.bankDetails) {
        setBankDetails(userData.bankDetails);
      }
      
      // Set UPI address if exists
      if (userData.upiAddress) {
        setUpiAddress(userData.upiAddress);
      }
      
      // Set profile photo if exists
      if (userData.profilePhoto) {
        setProfilePhoto(userData.profilePhoto);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load profile data');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await authAPI.updateProfile(profileData);
      
      // Update local storage
      const updatedUser = { ...user, ...profileData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      toast.success('Profile updated successfully!');
      await loadUserData();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankDetailsUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await authAPI.updateProfile({ bankDetails });
      toast.success('Bank details saved successfully!');
      await loadUserData();
    } catch (error) {
      console.error('Error updating bank details:', error);
      toast.error('Failed to update bank details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpiUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await authAPI.updateProfile({ upiAddress });
      toast.success('UPI address saved successfully!');
      await loadUserData();
    } catch (error) {
      console.error('Error updating UPI address:', error);
      toast.error('Failed to update UPI address');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a JPG image file only');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }
    
    setPhotoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    
    if (!photoFile) {
      toast.error('Please select an image first');
      return;
    }
    
    setIsLoading(true);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        
        await authAPI.updateProfile({ profilePhoto: base64String });
        toast.success('Profile photo updated successfully!');
        
        // Update local storage
        const updatedUser = { ...user, profilePhoto: base64String };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setProfilePhoto(base64String);
        
        await loadUserData();
      };
      reader.readAsDataURL(photoFile);
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      toast.error('Failed to upload profile photo');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Profile Settings</h1>
          <p className="text-gray-700 text-lg font-medium">Manage your account information and payment details</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="profile">
              <User className="h-4 w-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="bank">
              <CreditCard className="h-4 w-4 mr-2" />
              Bank Details
            </TabsTrigger>
            <TabsTrigger value="upi">
              <Smartphone className="h-4 w-4 mr-2" />
              UPI Address
            </TabsTrigger>
            <TabsTrigger value="photo">
              <Camera className="h-4 w-4 mr-2" />
              Profile Photo
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input
                        id="firstName"
                        value={profileData.firstName}
                        onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                        required
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        value={profileData.lastName}
                        onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                        required
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile Number</Label>
                    <Input
                      id="mobile"
                      type="tel"
                      value={profileData.mobile}
                      onChange={(e) => setProfileData({ ...profileData, mobile: e.target.value })}
                      placeholder="+91 9876543210"
                      className="h-12"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 text-white font-bold border-2 border-yellow-400"
                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Updating...' : 'Update Profile'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bank Details Tab */}
          <TabsContent value="bank">
            <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Bank Account Details</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBankDetailsUpdate} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="accountHolderName">Account Holder Name</Label>
                    <Input
                      id="accountHolderName"
                      value={bankDetails.accountHolderName}
                      onChange={(e) => setBankDetails({ ...bankDetails, accountHolderName: e.target.value })}
                      placeholder="As per bank records"
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accountNumber">Account Number</Label>
                    <Input
                      id="accountNumber"
                      value={bankDetails.accountNumber}
                      onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                      placeholder="1234567890"
                      className="h-12"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="ifscCode">IFSC Code</Label>
                      <Input
                        id="ifscCode"
                        value={bankDetails.ifscCode}
                        onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value })}
                        placeholder="SBIN0001234"
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bankName">Bank Name</Label>
                      <Input
                        id="bankName"
                        value={bankDetails.bankName}
                        onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                        placeholder="State Bank of India"
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="branchLocation">Branch Location</Label>
                    <Input
                      id="branchLocation"
                      value={bankDetails.branchLocation}
                      onChange={(e) => setBankDetails({ ...bankDetails, branchLocation: e.target.value })}
                      placeholder="Delhi, Connaught Place"
                      className="h-12"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 text-white font-bold border-2 border-yellow-400"
                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save Bank Details'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* UPI Tab */}
          <TabsContent value="upi">
            <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>UPI Address</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpiUpdate} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="upiAddress">UPI ID / VPA</Label>
                    <Input
                      id="upiAddress"
                      value={upiAddress}
                      onChange={(e) => setUpiAddress(e.target.value)}
                      placeholder="yourname@paytm / 9876543210@ybl"
                      className="h-12"
                    />
                    <p className="text-sm text-gray-600">
                      Enter your UPI ID for receiving payments (e.g., phone@paytm, username@okaxis)
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 text-white font-bold border-2 border-yellow-400"
                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save UPI Address'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Photo Tab */}
          <TabsContent value="photo">
            <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle>Profile Photo</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePhotoUpload} className="space-y-6">
                  {/* Current or Preview Photo */}
                  <div className="flex justify-center mb-6">
                    <img 
                      src={photoPreview || profilePhoto || 'https://via.placeholder.com/150?text=No+Photo'} 
                      alt="Profile" 
                      className="w-32 h-32 rounded-full object-cover border-4 border-red-400 shadow-lg"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/150?text=No+Image';
                      }}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="photoFile">Upload Profile Photo (JPG only)</Label>
                    <Input
                      id="photoFile"
                      type="file"
                      accept=".jpg,.jpeg,image/jpeg"
                      onChange={handlePhotoFileChange}
                      className="h-12 cursor-pointer"
                    />
                    <p className="text-sm text-gray-600">
                      📸 Select a JPG image file (Max size: 5MB)
                    </p>
                    {photoFile && (
                      <p className="text-sm text-green-600 font-medium">
                        ✅ Selected: {photoFile.name}
                      </p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 text-white font-bold border-2 border-yellow-400"
                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
                    disabled={isLoading || !photoFile}
                  >
                    {isLoading ? 'Uploading...' : 'Upload Profile Photo'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfileSettings;
